/**
 * @file PrivacyPage.jsx
 * @description A professionally designed, fully responsive dashboard page for managing domain and hosting details.
 *              Includes a Privacy Policy section detailing our policies.
 * @author
 *   Masimba Maregedze
 * @version 1.4.1
 */

import React, { useState, useEffect } from "react";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { runData } from "../functions/api/processor";
import PageHeader from "../components/PageHeader";

const PrivacyPage = () => {
  // State for SEO data, page content, loading, and error handling.
  const [seoData, setSeoData] = useState({
    title: "Loading...",
    meta_description: "",
    keywords: "",
    og_title: "",
    og_description: "",
    og_image: "",
    canonical_url: window.location.href,
  });
  const [pageContent, setPageContent] = useState(""); // State for the privacy content
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Fetch SEO and content data for the 'privacy' page
        const response = await runData({ page: "privacy" }, "/web/pages/", "GET");
        if (response.status === 200 && response.data) {
           // Set SEO data
           if (response.data.seo) {
            let seoResponse = response.data.seo;
            // Adjust canonical URL if needed
            if (
              seoResponse.canonical_url &&
              seoResponse.canonical_url.startsWith("/")
            ) {
              let hostname = window.location.hostname;
              if (!hostname.startsWith("www.")) {
                hostname = "www." + hostname;
              }
              seoResponse.canonical_url =
                window.location.protocol +
                "//" +
                hostname +
                seoResponse.canonical_url;
            }
            setSeoData(seoResponse);
          }
          // Set page content (assuming it's in response.data.content)
          if (response.data.content) {
            setPageContent(response.data.content);
          } else {
             // Fallback if content is missing
             setPageContent("<p>Privacy Policy content could not be loaded.</p>");
          }
        } else {
           throw new Error("Failed to load page data.");
        }
      } catch (err) {
        console.error("Failed to load page data:", err);
        setError(err.message || "An error occurred while loading page data.");
         // Set fallback content on error
         setPageContent("<p>Privacy Policy content could not be loaded due to an error.</p>");
         // Still try to set a default title if SEO failed
         if (seoData.title === "Loading...") {
             setSeoData(prev => ({ ...prev, title: "Privacy Policy" }));
         }
      } finally {
        setLoading(false);
      }
    })();
  }, []); // Empty dependency array ensures this runs only once on mount

  // Loading state
  if (loading) {
    return (
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  // Error state
  if (error && !pageContent) { // Only show full page error if content also failed
    return (
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        style={{ height: "100vh" }}
      >
        <h2>Error: {error}</h2>
      </Container>
    );
  }

  // Render the page with fetched data
  return (
    <React.Fragment>
      <Helmet>
        {/* SEO Meta Tags */}
        <title>{seoData.title} - Marmasco Technologies</title>
        <meta name="description" content={seoData.meta_description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta property="og:title" content={seoData.og_title} />
        <meta property="og:description" content={seoData.og_description} />
        <meta property="og:image" content={seoData.og_image} />
        <link rel="canonical" href={seoData.canonical_url} />
        {/* Schema Markup */}
        <script type="application/ld+json">
          {`
            {
              "@context": "http://schema.org",
              "@type": "WebPage",
              "name": "${seoData.title} - Marmasco Technologies",
              "url": "${seoData.canonical_url}",
              "description": "${seoData.meta_description}"
            }
          `}
        </script>
      </Helmet>

      {/* Header Section */}
      <PageHeader seoData={seoData} />

      {/* Main Content Section */}
      <Container
        id="content-start"
        className="bg-light py-5 fade-in mb-4 rounded-4 shadow-lg"
        style={{
          marginTop: "-6rem",
          borderRadius: "1rem",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Row>
          <Col className="px-md-4 px-2">
            {/* Render fetched HTML content */}
            {/* Ensure the fetched content is properly sanitized if it comes from untrusted sources */}
            <div dangerouslySetInnerHTML={{ __html: pageContent }} />
             {/* Display error message inline if content loading failed but SEO might have worked */}
             {error && pageContent.includes("could not be loaded") && (
                <p className="text-danger mt-3">Error loading content: {error}</p>
             )}
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default PrivacyPage;
